'use client';

import {
  createSignedSrcSetLoader,
  SignedSrcSetEntry,
} from '@lib/imgproxy/signedSrcSet';
import Image from 'next/image';
import { CSSProperties, memo, useCallback, useMemo, useState } from 'react';

export interface AppImageClientProps {
  /** Подписанные URL по ширинам из `getAppImageData` / `getSignedSrcSet`. */
  entries: SignedSrcSetEntry[];
  /** Blur-плейсхолдер до готовности основного изображения. */
  blurDataURL?: string;
  /** Отключает srcset (SVG). Выставляется в `getAppImageData`. */
  unoptimized?: boolean;
  alt?: string;
  width?: number;
  height?: number;
  fill?: boolean;
  sizes?: string;
  className?: string;
  style?: CSSProperties;
  priority?: boolean;
  loading?: 'eager' | 'lazy';
}

const BACKGROUND_SIZE_BY_FIT: Record<string, string> = {
  fill: '100% 100%',
  contain: 'contain',
  cover: 'cover',
  none: 'auto',
  'scale-down': 'contain',
};

const BACKGROUND_POSITION_BY_OBJECT: Record<string, string> = {
  'left-top': '0% 0%',
  'left-bottom': '0% 100%',
  'right-top': '100% 0%',
  'right-bottom': '100% 100%',
  center: '50% 50%',
  top: '50% 0%',
  bottom: '50% 100%',
  left: '0% 50%',
  right: '100% 50%',
};

const OBJECT_FIT_CLASS =
  /(?:^|\s)object-(fill|contain|cover|scale-down|none)(?=\s|$)/;
const OBJECT_POSITION_CLASS =
  /(?:^|\s)object-(left-top|left-bottom|right-top|right-bottom|center|top|bottom|left|right)(?=\s|$)/;

/** Уже отрисованные src: возврат к прежней картинке не должен снова показывать плейсхолдер. */
const loadedSources = new Set<string>();

const AppImageContent = ({
  entries,
  blurDataURL,
  unoptimized,
  alt = '',
  style,
  className,
  ...imageProps
}: AppImageClientProps) => {
  const src = entries[entries.length - 1].url;
  const loader = useMemo(() => createSignedSrcSetLoader(entries), [entries]);
  const [loaded, setLoaded] = useState(false);

  const complete = useCallback(() => {
    loadedSources.add(src);
    setLoaded(true);
  }, [src]);

  // Уже показанная картинка не должна снова уходить под плейсхолдер: состояние
  // снимаем в коммите, до отрисовки. onLoad для этого поздно — next/image зовёт
  // его только после decode(), то есть через кадр после готового кэша.
  const imgRef = useCallback(
    (node: HTMLImageElement | null) => {
      if (node?.complete || loadedSources.has(src)) {
        complete();
      }
    },
    [complete, src],
  );

  const placeholderStyle = useMemo(() => {
    if (!blurDataURL) {
      return undefined;
    }
    const fit = style?.objectFit ?? className?.match(OBJECT_FIT_CLASS)?.[1];
    const position =
      style?.objectPosition ??
      BACKGROUND_POSITION_BY_OBJECT[
        className?.match(OBJECT_POSITION_CLASS)?.[1] ?? 'center'
      ];

    return {
      backgroundImage: `url("${blurDataURL}")`,
      backgroundSize: BACKGROUND_SIZE_BY_FIT[fit ?? 'fill'] ?? '100% 100%',
      backgroundPosition: position,
      backgroundRepeat: 'no-repeat',
    } satisfies CSSProperties;
  }, [blurDataURL, style?.objectFit, style?.objectPosition, className]);

  return (
    <Image
      {...imageProps}
      ref={imgRef}
      className={className}
      src={src}
      loader={unoptimized ? undefined : loader}
      unoptimized={unoptimized}
      alt={alt}
      // Свой фон вместо placeholder="blur": SVG-фильтр next/image заливает
      // прозрачные области чёрным и растягивает плейсхолдер без учёта пропорций.
      placeholder="empty"
      style={loaded ? style : { ...placeholderStyle, ...style }}
      onLoad={complete}
      onError={complete}
    />
  );
};

/**
 * next/image поверх подписанных URL. Плейсхолдер — фон самого `img`: браузер
 * рисует его сразу из HTML, а декодированная картинка ложится сверху и
 * перекрывает, поэтому снятие фона не даёт отдельного кадра.
 *
 * Плейсхолдер должен совпадать с картинкой по геометрии, иначе подмена
 * заметна: `object-fit` и `object-position` читаются из `style` и из
 * tailwind-классов. Размеры и sizes берите из getAppImageData.
 */
export const AppImageClient = memo((props: AppImageClientProps) => (
  // Новый srcset получает собственное состояние плейсхолдера.
  <AppImageContent
    key={JSON.stringify([props.entries, props.blurDataURL])}
    {...props}
  />
));

AppImageClient.displayName = 'AppImageClient';
